import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { CommunityProfile } from '../components/sections/community-profile'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { ErrorButton } from '../components/ErrorButton'
import { supabaseBadgeService, type BadgeWithProgress, type UserBadgeWithDetails } from '../lib/supabaseBadgeService'

export function Community() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [profiles, setProfiles] = useState<any[]>([])
  const [votingHistory, setVotingHistory] = useState<any[]>([])
  const [allBadges, setAllBadges] = useState<BadgeWithProgress[]>([])
  const [userBadges, setUserBadges] = useState<UserBadgeWithDetails[]>([])

  // Fetch profiles and leaderboard data from Supabase
  useEffect(() => {
    async function fetchData() {
      setLoading(true)
      try {
        // Fetch all profiles with stats for leaderboard
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('*')
          .order('total_score', { ascending: false })
          .limit(50)

        if (profilesError) throw profilesError

        setProfiles(profilesData || [])

        // Fetch voting history and badge data for current user if logged in
        if (user) {
          const { data: votesData, error: votesError } = await supabase
            .from('votes')
            .select(`
              *,
              alternative:alternatives(
                *,
                foreign_word:foreign_words(*)
              )
            `)
            .eq('user_id', user.id)
            .order('voted_at', { ascending: false })
            .limit(20)

          if (!votesError) {
            setVotingHistory(votesData || [])
          }

          // Fetch badge data
          try {
            const [badgesData, userBadgesData] = await Promise.all([
              supabaseBadgeService.getAllBadgesWithProgress(user.id),
              supabaseBadgeService.getUserBadges(user.id)
            ])
            setAllBadges(badgesData)
            setUserBadges(userBadgesData)
          } catch (badgeError) {
            console.error('Failed to load badge data:', badgeError)
          }
        }
      } catch (error) {
        console.error('Error fetching community data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [user])

  const handleViewProfile = (userId: string) => {
    navigate(`/profile/${userId}`)
  }

  const handleViewVotingHistory = (userId: string) => {
    console.log('View voting history:', userId)
    toast(`عرض تاريخ التصويت للعضو: ${userId}`)
  }

  // Map profiles to users format with all required fields
  const users = profiles.map(profile => ({
    id: profile.id,
    name: profile.name,
    avatarUrl: profile.avatar_url,
    joinDate: profile.join_date || profile.created_at,
    wordsSubmitted: profile.words_submitted || 0,
    alternativesProposed: profile.alternatives_proposed || 0,
    votesCast: profile.votes_cast || 0,
    commentsLeft: profile.comments_left || 0,
    totalScore: profile.total_score || 0,
    rank: 0,
  }))

  // Map profiles to userProfiles format for detailed info
  const userProfilesRecord = profiles.reduce((acc, profile) => {
    acc[profile.id] = {
      id: profile.id,
      name: profile.name,
      avatarUrl: profile.avatar_url,
      joinDate: profile.join_date || profile.created_at,
      wordsSubmitted: profile.words_submitted || 0,
      alternativesProposed: profile.alternatives_proposed || 0,
      votesCast: profile.votes_cast || 0,
      commentsLeft: profile.comments_left || 0,
      totalScore: profile.total_score || 0,
      rank: 0,
      bio: profile.bio || '',
      achievements: [],
      recentActivity: [],
    }
    return acc
  }, {} as Record<string, any>)

  // Create leaderboard entries
  const createLeaderboardEntry = (profile: any, rank: number) => ({
    userId: profile.id,
    name: profile.name,
    avatarUrl: profile.avatar_url,
    totalScore: profile.total_score || 0,
    rank: rank + 1,
  })

  const createLeaderboardWithCount = (profile: any, rank: number, count: number) => ({
    userId: profile.id,
    name: profile.name,
    avatarUrl: profile.avatar_url,
    count,
    rank: rank + 1,
  })

  // Create leaderboards from profiles
  const leaderboards = {
    overall: profiles.slice(0, 10).map((profile, index) => createLeaderboardEntry(profile, index)),
    wordsSubmitted: [...profiles]
      .sort((a, b) => (b.words_submitted || 0) - (a.words_submitted || 0))
      .slice(0, 10)
      .map((profile, index) => createLeaderboardWithCount(profile, index, profile.words_submitted || 0)),
    alternativesProposed: [...profiles]
      .sort((a, b) => (b.alternatives_proposed || 0) - (a.alternatives_proposed || 0))
      .slice(0, 10)
      .map((profile, index) => createLeaderboardWithCount(profile, index, profile.alternatives_proposed || 0)),
    votesCast: [...profiles]
      .sort((a, b) => (b.votes_cast || 0) - (a.votes_cast || 0))
      .slice(0, 10)
      .map((profile, index) => createLeaderboardWithCount(profile, index, profile.votes_cast || 0)),
  }

  // Map voting history to the expected format
  const votingHistoryMap: Record<string, any> = {}
  votingHistory.forEach(vote => {
    votingHistoryMap[vote.id] = {
      id: vote.id,
      userId: user?.id || '',
      alternativeId: vote.alternative_id,
      foreignWord: vote.alternative?.foreign_word?.word || '',
      foreignWordArabic: vote.alternative?.foreign_word?.word_arabic || '',
      alternativeText: vote.alternative?.egyptian_word || '',
      voteType: vote.vote_type,
      votedAt: vote.voted_at,
    }
  })

  // Map current user - look up their profile from the fetched profiles
  const currentUser = user ? (() => {
    // Find the current user's profile in the profiles array
    const userProfile = profiles.find(p => p.id === user.id)

    if (userProfile) {
      // Calculate rank based on position in the sorted profiles array
      const rank = profiles.findIndex(p => p.id === user.id) + 1

      return {
        id: userProfile.id,
        name: userProfile.name || user.user_metadata?.name || user.email?.split('@')[0] || 'User',
        avatarUrl: userProfile.avatar_url || user.user_metadata?.avatar_url,
        joinDate: userProfile.join_date || userProfile.created_at,
        wordsSubmitted: userProfile.words_submitted || 0,
        alternativesProposed: userProfile.alternatives_proposed || 0,
        votesCast: userProfile.votes_cast || 0,
        commentsLeft: userProfile.comments_left || 0,
        totalScore: userProfile.total_score || 0,
        rank: rank,
      }
    }

    // Fallback if user profile not found in database yet
    return {
      id: user.id,
      name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
      avatarUrl: user.user_metadata?.avatar_url,
      joinDate: new Date().toISOString(),
      wordsSubmitted: 0,
      alternativesProposed: 0,
      votesCast: 0,
      commentsLeft: 0,
      totalScore: 0,
      rank: 0,
    }
  })() : {
    id: '',
    name: 'Guest',
    avatarUrl: null,
    joinDate: new Date().toISOString(),
    wordsSubmitted: 0,
    alternativesProposed: 0,
    votesCast: 0,
    commentsLeft: 0,
    totalScore: 0,
    rank: 0,
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-slate-400">جاري التحميل...</div>
      </div>
    )
  }

  return (
    <div>
      <CommunityProfile
        users={users}
        userProfiles={userProfilesRecord}
        leaderboards={leaderboards}
        votingHistory={votingHistoryMap}
        currentUser={currentUser}
        isOwnProfile={true}
        allBadges={allBadges}
        userBadges={userBadges}
        onViewProfile={handleViewProfile}
        onViewVotingHistory={handleViewVotingHistory}
      />

      {/* Development error testing section */}
      {import.meta.env.DEV && (
        <div className="p-6 mt-8 border rounded-lg bg-slate-800 border-slate-700">
          <h3 className="mb-4 text-lg font-bold text-slate-100">🧪 Error Testing (Development Only)</h3>
          <p className="mb-4 text-sm text-slate-400">
            Use these buttons to test Sentry error tracking. They will only appear in development mode.
          </p>
          <div className="flex flex-wrap gap-4">
            <ErrorButton />
          </div>
        </div>
      )}
    </div>
  )
}
